import React, { useState, useRef, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import HelpModal from '../../HelpModal';
import { FaPlay, FaPause, FaVolumeUp, FaVolumeMute } from 'react-icons/fa';
import HEXASLogo from '../../assets/Logos/HEXASLogo.jpg';
import GuidedPractice01FormAudio from '../../assets/assets/listening/GuidedPractice01Form.mp3';
import { useTextHighlight } from '../../utils/highlightUtils';

const GuidedPractice02FormTable = () => {
  const navigate = useNavigate();

  const [showHelp, setShowHelp] = useState(false);

  const questionRefs = useRef([]);
  const rightColRef = useRef(null);

  // Make sure your answers state exists
  const [answers, setAnswers] = useState(Array(10).fill(''));
  const [currentQuestion, setCurrentQuestion] = useState(1);
  const onNext = () => {
    setCurrentQuestion(prev => Math.min(prev + 1, 10));
  };

  const audioRef = useRef(null);
  const [isPlaying, setIsPlaying] = useState(false);
  const [currentTime, setCurrentTime] = useState(0);
  const [duration, setDuration] = useState(0);
  const [volume, setVolume] = useState(1);
  const [isMuted, setIsMuted] = useState(false);

  const handleStartTest = () => {
    navigate(`/guided-practice-01-form`);
  };

  const handlePlayPause = () => {
    if (!audioRef.current) return;
    if (isPlaying) {
      audioRef.current.pause();
    } else {
      audioRef.current.play();
    }
    setIsPlaying(!isPlaying);
  };

  const handleTimeUpdate = () => {
    if (!audioRef.current) return;
    setCurrentTime(audioRef.current.currentTime);
  };

  const handleLoadedMetadata = () => {
    if (!audioRef.current) return;
    setDuration(audioRef.current.duration);
  };

  const handleSeek = (e) => {
    if (!audioRef.current) return;
    const time = parseFloat(e.target.value);
    audioRef.current.currentTime = time;
    setCurrentTime(time);
  };

  const handleVolumeChange = (e) => {
    if (!audioRef.current) return;
    const vol = parseFloat(e.target.value);
    setVolume(vol);
    audioRef.current.volume = vol;
    setIsMuted(vol === 0);
  };

  const handleMute = () => {
    if (!audioRef.current) return;
    if (isMuted) {
      audioRef.current.muted = false;
      setIsMuted(false);
      audioRef.current.volume = volume;
    } else {
      audioRef.current.muted = true;
      setIsMuted(true);
    }
  };

  // Timer state
  const [timeRemaining, setTimeRemaining] = useState(100 * 60); // 30 minutes in seconds
  const [showSeconds, setShowSeconds] = useState(false);

  // Timer effect
  useEffect(() => {
    const timer = setInterval(() => {
      setTimeRemaining(prev => {
        if (prev <= 0) {
          clearInterval(timer);
          navigate('/');  // Redirect to home
          return 0;
        }
        return prev - 1;
      });
    }, 1000);

    return () => clearInterval(timer);
  }, [navigate]);

  // Format time function
  const formatTime = (seconds) => {
    const mins = Math.floor(seconds / 60);
    const secs = seconds % 60;

    if (showSeconds) {
      return `${mins} : ${secs.toString().padStart(2, '0')} remaining`;
    }
    return `${mins} minutes remaining`;
  };

  const highlightRef = useRef(null);
  useTextHighlight(highlightRef);

  return (
    <div className="min-h-screen bg-white flex flex-col font-[Arial]">
      {/* Help Modal */}
      <HelpModal
        isOpen={showHelp}
        onClose={() => setShowHelp(false)}
      />

      {/* Top Bar (fixed) */}
      <div className="sticky top-0 z-30 bg-white">

        <div className="relative h-[62px] bg-[#F4C3C3] border-b border-black flex items-center justify-between px-4">
          {/* Left + Center combined on left side */}
          <div className="flex flex-col justify-center space-y-0.5 pl-6">
            <span className="text-[14px] text-left text-black font-bold leading-tight">
              HEXA'S Education
            </span>
            <div className="flex items-center space-x-6 text-[14px] text-black font-medium">
              <span
                onMouseEnter={() => setShowSeconds(true)}
                onMouseLeave={() => setShowSeconds(false)}
                className="cursor-pointer"
              >
                {formatTime(timeRemaining)}
              </span>
            </div>
          </div>

          {/* Right: Icons and Finish Button */}
          <div className="flex items-center space-x-5">
            <button
              onClick={() => navigate('/')}
              className="text-black border border-black rounded-md px-4 py-1 text-[14px]  hover:bg-gray-100"
            >
              Back Home
            </button>
            <button
              onClick={() => setShowHelp(true)}
              className="text-black border border-black rounded-md px-4 py-1 text-[14px]  hover:bg-gray-100"
            >
              Finish test
            </button>

            {/* Icons container with larger size and spacing */}
            <div className="flex items-center space-x-8 pl-2 pr-2">
              {/* WiFi Icon */}
              <div className="w-10 h-10 flex items-center justify-center">
                <svg className="w-9 h-9 text-black" fill="currentColor" viewBox="0 0 20 20">
                  <path
                    fillRule="evenodd"
                    d="M17.778 8.222c-4.296-4.296-11.26-4.296-15.556 0A1 1 0 010.808 6.808c5.076-5.077 13.308-5.077 18.384 0a1 1 0 01-1.414 1.414zM14.95 11.05a7 7 0 00-9.9 0 1 1 0 01-1.414-1.414 9 9 0 0112.728 0 1 1 0 01-1.414 1.414zM12.12 13.88a3 3 0 00-4.24 0 1 1 0 01-1.415-1.415 5 5 0 017.07 0 1 1 0 01-1.415 1.415zM9 16a1 1 0 011-1h.01a1 1 0 110 2H10a1 1 0 01-1-1z"
                    clipRule="evenodd"
                  />
                </svg>
              </div>

              {/* Notification Bell Icon */}
              <div className="w-10 h-10 flex items-center justify-center">
                <svg className="w-8 h-8 text-black" fill="currentColor" viewBox="0 0 20 20">
                  <path d="M10 2a6 6 0 00-6 6v3.586l-.707.707A1 1 0 004 14h12a1 1 0 00.707-1.707L16 11.586V8a6 6 0 00-6-6zM10 18a3 3 0 01-3-3h6a3 3 0 01-3 3z" />
                </svg>
              </div>

              {/* Hamburger Menu Icon */}
              <div className="w-10 h-10 flex items-center justify-center">
                <svg className="w-8 h-8 text-black" fill="currentColor" viewBox="0 0 20 20">
                  <path
                    fillRule="evenodd"
                    d="M3 5a1 1 0 011-1h12a1 1 0 110 2H4a1 1 0 01-1-1zM3 10a1 1 0 011-1h12a1 1 0 110 2H4a1 1 0 01-1-1zM3 15a1 1 0 011-1h12a1 1 0 110 2H4a1 1 0 01-1-1z"
                    clipRule="evenodd"
                  />
                </svg>
              </div>

              {/* Pen/Edit Icon */}
              <div className="w-10 h-10 flex items-center justify-center">
                <svg className="w-8 h-8 text-black" fill="currentColor" viewBox="0 0 20 20">
                  <path d="M13.586 3.586a2 2 0 112.828 2.828l-.793.793-2.828-2.828.793-.793zM11.379 5.793L3 14.172V17h2.828l8.38-8.379-2.83-2.828z" />
                </svg>
              </div>
            </div>
          </div>
        </div>

        <div className="relative bg-[#F4F7FA] shadow-sm border border-black rounded-lg mx-12 mt-4 mb-0 sticky top-[32px] z-20 flex items-center justify-between">
          {/* Left: Part Title */}
          <div className="flex-1 px-4 py-2 text-left">
            <h2 className="text-[23px] text-black">Part 1</h2>
            <h1 className="text-[18px] text-black">Cambridge 15 Listening Test 04</h1>
          </div>

          {/* Center: Absolutely Positioned Logo */}
          <div className="absolute left-1/2 top-1/2 transform -translate-x-1/2 -translate-y-1/2">
            <img
              src={HEXASLogo}
              alt="HEXA'S Logo"
              className="h-[50px] object-contain"
            />
          </div>

          {/* Right: Audio Player */}
          <div className="flex items-center justify-end w-[300px] pr-4">
            <div className="bg-[#0E7ACD] rounded-xl flex items-center px-3 py-1 w-full max-w-[420px] h-[36px]">
              <button onClick={handlePlayPause} className="text-white mr-2 focus:outline-none">
                {isPlaying ? <FaPause size={14} /> : <FaPlay size={14} />}
              </button>
              <input
                type="range"
                min={0}
                max={duration}
                step={0.01}
                value={currentTime}
                onChange={handleSeek}
                className="flex-1 mx-2 accent-blue-200 h-[4px]"
                style={{ minWidth: 100 }}
              />
              <button onClick={handleMute} className="text-white mr-1 focus:outline-none">
                {isMuted || volume === 0 ? <FaVolumeMute size={12} /> : <FaVolumeUp size={12} />}
              </button>
              <input
                type="range"
                min={0}
                max={1}
                step={0.01}
                value={isMuted ? 0 : volume}
                onChange={handleVolumeChange}
                className="accent-blue-200 h-[4px]"
                style={{ width: 50 }}
              />
              <audio
                ref={audioRef}
                src={GuidedPractice01FormAudio}
                onTimeUpdate={handleTimeUpdate}
                onLoadedMetadata={handleLoadedMetadata}
                onEnded={() => setIsPlaying(false)}
                onError={(e) => console.error("Audio error", e)}
                style={{ display: 'none' }}
              />
            </div>
          </div>
        </div>
      </div>

      {/* Main content (scrollable) */}
      <div className="flex-1 flex flex-col items-center justify-start w-full">
        <div className="w-full px-12 py-2 flex justify-start">

          {/* Highlightable content wrapper */}
          <div ref={highlightRef} className="bg-white  shadow-sm p-6 w-full max-w-[800px] text-left overflow-y-auto" style={{ maxHeight: 'calc(100vh - 180px)' }}>
            <h2 className="text-[18px] font-bold text-black mb-2 ">Questions 1 – 10</h2>
            <p className="text-[14px] mb-2">Complete the form below.</p>
            <p className="text-[14px] mb-4">Write <span className="font-bold">ONE WORD AND/OR A NUMBER</span> for each answer.</p>
            <h3 className="text-[16px] font-bold text-black mb-2 mt-4 text-center">Customer Satisfaction Survey</h3>
            {/* Customer details */}
            <h4 className="text-[15px] font-semibold text-black mb-2 mt-4">Customer details</h4>
            <div className="mb-4">
              <div className="grid grid-cols-3 gap-2 items-center mb-2">
                <div className="col-span-1 text-[14px]">Name:</div>
                <div className="col-span-2 text-[14px] font-semibold">Sophie Bird</div>
              </div>
              <div className="grid grid-cols-3 gap-2 items-center mb-2">
                <div className="col-span-1 text-[14px]">Occupation:</div>
                <div className="col-span-2">
                  <span className="inline-block align-middle rounded bg-[#E0F2FE] border border-[#7DD3FC] px-1 py-0 mx-1" style={{ minWidth: 105, minHeight: 26 }}>
                    <input
                      ref={el => questionRefs.current[0] = el}
                      type="text"
                      placeholder="1"
                      className="w-full h-full bg-transparent text-center text-[13px] font-bold outline-none placeholder:font-bold placeholder:text-black placeholder:opacity-100 focus:placeholder:opacity-0"
                      value={answers[0]}
                      onChange={e => {
                        const newAnswers = [...answers];
                        newAnswers[0] = e.target.value;
                        setAnswers(newAnswers);
                      }}
                    />
                  </span>
                </div>
              </div>
              <div className="grid grid-cols-3 gap-2 items-center mb-2">
                <div className="col-span-1 text-[14px]">Reason for travel today:</div>
                <div className="col-span-2">
                  <span className="inline-block align-middle rounded bg-[#E0F2FE] border border-[#7DD3FC] px-1 py-0 mx-1" style={{ minWidth: 105, minHeight: 26 }}>
                    <input
                      ref={el => questionRefs.current[1] = el}
                      type="text"
                      placeholder="2"
                      className="w-full h-full bg-transparent text-center text-[13px] font-bold outline-none placeholder:font-bold placeholder:text-black placeholder:opacity-100 focus:placeholder:opacity-0"
                      value={answers[1]}
                      onChange={e => {
                        const newAnswers = [...answers];
                        newAnswers[1] = e.target.value;
                        setAnswers(newAnswers);
                      }}
                    />
                  </span>
                </div>
              </div>
            </div>
            {/* Journey information */}
            <h4 className="text-[15px] font-semibold text-black mb-2 mt-4">Journey information</h4>
            <div className="mb-4">
              <div className="grid grid-cols-3 gap-2 items-center mb-2">
                <div className="col-span-1 text-[14px]">Name of station returning to:</div>
                <div className="col-span-2">
                  <span className="inline-block align-middle rounded bg-[#E0F2FE] border border-[#7DD3FC] px-1 py-0 mx-1" style={{ minWidth: 105, minHeight: 26 }}>
                    <input
                      ref={el => questionRefs.current[2] = el}
                      type="text"
                      placeholder="3"
                      className="w-full h-full bg-transparent text-center text-[13px] font-bold outline-none placeholder:font-bold placeholder:text-black placeholder:opacity-100 focus:placeholder:opacity-0"
                      value={answers[2]}
                      onChange={e => {
                        const newAnswers = [...answers];
                        newAnswers[2] = e.target.value;
                        setAnswers(newAnswers);
                      }}
                    />
                  </span>
                </div>
              </div>
              <div className="grid grid-cols-3 gap-2 items-center mb-2">
                <div className="col-span-1 text-[14px]">Type of ticket purchased:</div>
                <div className="col-span-2 flex items-center">
                  <span className="mr-1">Standard</span>
                  <span className="inline-block align-middle rounded bg-[#E0F2FE] border border-[#7DD3FC] px-1 py-0 mx-1" style={{ minWidth: 105, minHeight: 26 }}>
                    <input
                      ref={el => questionRefs.current[3] = el}
                      type="text"
                      placeholder="4"
                      className="w-full h-full bg-transparent text-center text-[13px] font-bold outline-none placeholder:font-bold placeholder:text-black placeholder:opacity-100 focus:placeholder:opacity-0"
                      value={answers[3]}
                      onChange={e => {
                        const newAnswers = [...answers];
                        newAnswers[3] = e.target.value;
                        setAnswers(newAnswers);
                      }}
                    />
                  </span>
                  <span className="ml-1">ticket</span>
                </div>
              </div>
              <div className="grid grid-cols-3 gap-2 items-center mb-2">
                <div className="col-span-1 text-[14px]">Cost of ticket:</div>
                <div className="col-span-2 flex items-center">
                  <span className="mr-1">£</span>
                  <span className="inline-block align-middle rounded bg-[#E0F2FE] border border-[#7DD3FC] px-1 py-0 mx-1" style={{ minWidth: 105, minHeight: 26 }}>
                    <input
                      ref={el => questionRefs.current[4] = el}
                      type="text"
                      placeholder="5"
                      className="w-full h-full bg-transparent text-center text-[13px] font-bold outline-none placeholder:font-bold placeholder:text-black placeholder:opacity-100 focus:placeholder:opacity-0"
                      value={answers[4]}
                      onChange={e => {
                        const newAnswers = [...answers];
                        newAnswers[4] = e.target.value;
                        setAnswers(newAnswers);
                      }}
                    />
                  </span>
                </div>
              </div>
              <div className="grid grid-cols-3 gap-2 items-center mb-2">
                <div className="col-span-1 text-[14px]">When ticket was purchased:</div>
                <div className="col-span-2 text-[14px]">Yesterday</div>
              </div>
              <div className="grid grid-cols-3 gap-2 items-center mb-2">
                <div className="col-span-1 text-[14px]">Where ticket was bought:</div>
                <div className="col-span-2">
                  <span className="inline-block align-middle rounded bg-[#E0F2FE] border border-[#7DD3FC] px-1 py-0 mx-1" style={{ minWidth: 105, minHeight: 26 }}>
                    <input
                      ref={el => questionRefs.current[5] = el}
                      type="text"
                      placeholder="6"
                      className="w-full h-full bg-transparent text-center text-[13px] font-bold outline-none placeholder:font-bold placeholder:text-black placeholder:opacity-100 focus:placeholder:opacity-0"
                      value={answers[5]}
                      onChange={e => {
                        const newAnswers = [...answers];
                        newAnswers[5] = e.target.value;
                        setAnswers(newAnswers);
                      }}
                    />
                  </span>
                </div>
              </div>
            </div>
            {/* Satisfaction with journey */}
            <h4 className="text-[15px] font-semibold text-black mb-2 mt-4">Satisfaction with journey</h4>
            <div className="mb-4">
              <div className="grid grid-cols-3 gap-2 items-center mb-2">
                <div className="col-span-1 text-[14px]">Most satisfied with:</div>
                <div className="col-span-2 text-[14px]">the wifi</div>
              </div>
              <div className="grid grid-cols-3 gap-2 items-center mb-2">
                <div className="col-span-1 text-[14px]">Least satisfied with:</div>
                <div className="col-span-2 flex items-center">
                  <span className="mr-1">the</span>
                  <span className="inline-block align-middle rounded bg-[#E0F2FE] border border-[#7DD3FC] px-1 py-0 mx-1" style={{ minWidth: 105, minHeight: 26 }}>
                    <input
                      ref={el => questionRefs.current[6] = el}
                      type="text"
                      placeholder="7"
                      className="w-full h-full bg-transparent text-center text-[13px] font-bold outline-none placeholder:font-bold placeholder:text-black placeholder:opacity-100 focus:placeholder:opacity-0"
                      value={answers[6]}
                      onChange={e => {
                        const newAnswers = [...answers];
                        newAnswers[6] = e.target.value;
                        setAnswers(newAnswers);
                      }}
                    />
                  </span>
                  <span className="ml-1">this morning.</span>
                </div>
              </div>
            </div>
            {/* Satisfaction with station facilities */}
            <h4 className="text-[15px] font-semibold text-black mb-2 mt-4">Satisfaction with station facilities</h4>
            <div className="mb-4">
              <div className="grid grid-cols-3 gap-2 items-center mb-2">
                <div className="col-span-1 text-[14px]">Most satisfied with:</div>
                <div className="col-span-2 flex items-center">
                  <span className="mr-1">how much</span>
                  <span className="inline-block align-middle rounded bg-[#E0F2FE] border border-[#7DD3FC] px-1 py-0 mx-1" style={{ minWidth: 105, minHeight: 26 }}>
                    <input
                      ref={el => questionRefs.current[7] = el}
                      type="text"
                      placeholder="8"
                      className="w-full h-full bg-transparent text-center text-[13px] font-bold outline-none placeholder:font-bold placeholder:text-black placeholder:opacity-100 focus:placeholder:opacity-0"
                      value={answers[7]}
                      onChange={e => {
                        const newAnswers = [...answers];
                        newAnswers[7] = e.target.value;
                        setAnswers(newAnswers);
                      }}
                    />
                  </span>
                  <span className="ml-1">was provided</span>
                </div>
              </div>
              <div className="grid grid-cols-3 gap-2 items-center mb-2">
                <div className="col-span-1 text-[14px]">Least satisfied with:</div>
                <div className="col-span-2 flex items-center">
                  <span className="mr-1">lack of seats, particularly on the</span>
                  <span className="inline-block align-middle rounded bg-[#E0F2FE] border border-[#7DD3FC] px-1 py-0 mx-1" style={{ minWidth: 105, minHeight: 26 }}>
                    <input
                      ref={el => questionRefs.current[8] = el}
                      type="text"
                      placeholder="9"
                      className="w-full h-full bg-transparent text-center text-[13px] font-bold outline-none placeholder:font-bold placeholder:text-black placeholder:opacity-100 focus:placeholder:opacity-0"
                      value={answers[8]}
                      onChange={e => {
                        const newAnswers = [...answers];
                        newAnswers[8] = e.target.value;
                        setAnswers(newAnswers);
                      }}
                    />
                  </span>
                </div>
              </div>
              <div className="grid grid-cols-3 gap-2 items-center mb-2">
                <div className="col-span-1 text-[14px]">Neither satisfied nor dissatisfied with:</div>
                <div className="col-span-2 flex items-center">
                  <span className="mr-1">the</span>
                  <span className="inline-block align-middle rounded bg-[#E0F2FE] border border-[#7DD3FC] px-1 py-0 mx-1" style={{ minWidth: 105, minHeight: 26 }}>
                    <input
                      ref={el => questionRefs.current[9] = el}
                      type="text"
                      placeholder="10"
                      className="w-full h-full bg-transparent text-center text-[13px] font-bold outline-none placeholder:font-bold placeholder:text-black placeholder:opacity-100 focus:placeholder:opacity-0"
                      value={answers[9]}
                      onChange={e => {
                        const newAnswers = [...answers];
                        newAnswers[9] = e.target.value;
                        setAnswers(newAnswers);
                      }}
                    />
                  </span>
                  <span className="ml-1">available</span>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Bottom Navigation (fixed footer) */}
      <div className="sticky bottom-0 left-0 w-full bg-white border-t border-gray-300 z-30 flex items-center justify-between px-4 py-3">

        {/* Left: Question Buttons */}
        <div className="flex items-center space-x-1">
          {Array.from({ length: 10 }, (_, i) => (
            <button
              key={i + 1}
              onClick={() => {
                setCurrentQuestion(i + 1);
                setTimeout(() => {
                  const ref = questionRefs.current[i];
                  if (ref) {
                    ref.scrollIntoView({ behavior: 'smooth', block: 'center' });
                    ref.focus();
                  }
                }, 0);
              }}
              className={`w-6 h-6 mx-0.5 rounded ${currentQuestion === i + 1 ? 'bg-blue-500 text-white' : 'bg-black text-white'
                } text-[12px] font-bold flex items-center justify-center`}
            >
              {i + 1}
            </button>
          ))}
        </div>

        {/* Right: Arrows */}
        <div className="flex items-center space-x-2">
          {/* Backward Arrow */}
          <button
            onClick={() => {
              setCurrentQuestion((q) => Math.max(1, q - 1));
              setTimeout(() => {
                const ref = questionRefs.current[currentQuestion - 2];
                if (ref) {
                  ref.scrollIntoView({ behavior: 'smooth', block: 'center' });
                  ref.focus();
                }
              }, 0);
            }}
            className="w-7 h-7 flex items-center justify-center rounded-full bg-[#2B7BB9] text-white hover:bg-[#2468A0] transition-colors"
            title="Previous Question"
            disabled={currentQuestion === 1}
          >
            <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke="currentColor" className="w-4 h-4">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
            </svg>
          </button>

          {/* Forward Arrow */}
          <button
            onClick={() => {
              setCurrentQuestion((q) => Math.min(10, q + 1));
              setTimeout(() => {
                const ref = questionRefs.current[currentQuestion];
                if (ref) {
                  ref.scrollIntoView({ behavior: 'smooth', block: 'center' });
                  ref.focus();
                }
              }, 0);
            }}
            className="w-7 h-7 flex items-center justify-center rounded-full bg-[#2B7BB9] text-white hover:bg-[#2468A0] transition-colors"
            title="Next Question"
            disabled={currentQuestion === 10}
          >
            <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke="currentColor" className="w-4 h-4">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
            </svg>
          </button>
        </div>
      </div>


    </div>
  );
};

export default GuidedPractice02FormTable;
